<template>
  <Modal
    :value="value"
    :title="title"
    :cta-text="ctaText || $t('generic.buttons.apply')"
    content-class="page-picker-modal"
    persistent
    @cancel="$emit('input', false)"
    @confirm="confirm">
    <template #default>
      <v-autocomplete
        v-model="pickedPages"
        :items="items"
        :search-input.sync="search"
        :loading="loading"
        :placeholder="placeholder"
        item-text="slug"
        item-value="@id"
        return-object
        multiple
        hide-details
        :menu-props="{ offsetY: true }">
        <template #selection><span></span></template>
        <template #append-item>
          <div v-if="hasMore" v-intersect="onLoadMoreIntersect" class="page-picker-load-more">
            <v-progress-circular v-if="loading" indeterminate size="18" width="2" color="primary" />
          </div>
        </template>
      </v-autocomplete>
      <div class="page-picker-chips" v-if="pickedPages.length">
        <v-chip
          v-for="page in pickedPages"
          :key="page['@id']"
          color="primary-20"
          text-color="secondary-btn"
          close
          close-icon="mdi-close"
          @click:close="unpick(page)">
          {{ truncateText(page.slug) }}
        </v-chip>
      </div>
      <div class="page-picker-warning" v-if="selectedInUseElsewhere.length && inUseMessage">
        <v-icon small left color="warning">mdi-alert-outline</v-icon>
        <span>
          {{ inUseMessage }}
          <strong :key="page['@id']" v-for="(page, index) in selectedInUseElsewhere">
            {{ truncateText(page.slug) }}<template v-if="index < selectedInUseElsewhere.length - 1">, </template>
          </strong>
        </span>
      </div>
      <div class="page-picker-note" v-if="note">
        <v-icon small left>mdi-information-outline</v-icon>
        <span>{{ note }}</span>
      </div>
    </template>
  </Modal>
</template>

<script>
  import Modal from "@/components/custom/Modal";
  import debounce from "lodash.debounce";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { truncateText } from "@/utility/truncateText";
  import { PAGE_STATUS_DRAFT, PAGE_STATUS_PUBLISHED } from "@/constant";

  export default {
    name: "PagePickerModal",
    components: { Modal },
    props: {
      value: { type: Boolean, required: true },
      domain: { type: String, default: null },
      pages: { type: Array, default: () => [] },
      title: { type: String, required: true },
      placeholder: { type: String, default: "" },
      note: { type: String, default: "" },
      ctaText: { type: String, default: "" },
      inUseField: { type: String, default: "" },
      currentResourceIri: { type: String, default: null },
      inUseMessage: { type: String, default: "" },
    },
    data() {
      return {
        pickedPages: [],
        items: [],
        search: null,
        loading: false,
        fetchToken: 0,
        page: 1,
        hasMore: true,
        perPage: 20,
      };
    },
    computed: {
      domainFilter() {
        if (!this.domain) return null;
        return {
          domain: this.domain.replace("/api/domains/", ""),
          status: [PAGE_STATUS_DRAFT, PAGE_STATUS_PUBLISHED],
        };
      },
      selectedInUseElsewhere() {
        if (!this.inUseField) return [];
        return this.pickedPages.filter(p => {
          const attached = p[this.inUseField];
          if (!attached) return false;
          const attachedIri = typeof attached === "string" ? attached : attached["@id"];
          return attachedIri && attachedIri !== this.currentResourceIri;
        });
      },
    },
    watch: {
      value(open) {
        if (open) {
          this.pickedPages = (this.pages || []).map(this.toAutocompleteItem);
          this.search = null;
          this.resetAndLoadPages();
        } else {
          this.fetchToken += 1;
          this.pickedPages = [];
          this.items = [];
          this.search = null;
          this.loading = false;
          this.page = 1;
          this.hasMore = true;
        }
      },
      search: debounce(function () {
        if (!this.value) return;
        this.resetAndLoadPages();
      }, 400),
    },
    methods: {
      truncateText,
      toAutocompleteItem(page) {
        // v-autocomplete reserves the `header` key for list subheaders, so we
        // rename `header`/`footer` instead of dropping them — keeps the
        // current-attachment IRIs available to consumers as
        // `currentHeader` / `currentFooter`.
        const { header, footer, ...rest } = page;
        return { ...rest, currentHeader: header, currentFooter: footer };
      },
      resetAndLoadPages() {
        this.page = 1;
        this.hasMore = true;
        this.items = this.pickedPages.slice();
        this.loadMorePages();
      },
      async loadMorePages() {
        if (!this.domainFilter || !this.hasMore) return;
        const token = ++this.fetchToken;
        const isStale = () => token !== this.fetchToken;
        this.loading = true;
        try {
          const filter = { ...this.domainFilter };
          if (this.search) filter.slug = this.search;
          const { data = [] } = await this.$admin.dataProvider.getList("pages", {
            filter,
            pagination: { page: this.page, perPage: this.perPage },
            sort: [{ by: "slug", desc: false }],
          });
          if (isStale()) return;
          const fetched = data.map(this.toAutocompleteItem);
          const seen = new Set(this.items.map(p => p["@id"]));
          this.items = [...this.items, ...fetched.filter(p => !seen.has(p["@id"]))];
          this.hasMore = fetched.length >= this.perPage;
          this.page += 1;
        } catch (e) {
          if (!isStale()) this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          if (!isStale()) this.loading = false;
        }
      },
      onLoadMoreIntersect(_entries, _observer, isIntersecting) {
        if (!isIntersecting || this.loading || !this.hasMore) return;
        this.loadMorePages();
      },
      unpick(page) {
        this.pickedPages = this.pickedPages.filter(p => p["@id"] !== page["@id"]);
      },
      confirm() {
        const result = this.pickedPages.map(p => ({ "@id": p["@id"], slug: p.slug }));
        this.$emit("confirm", result);
        this.$emit("input", false);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-picker-chips {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 6px;
    margin-top: 12px;
  }

  .page-picker-note {
    @include text-caption();
    color: get-color("body");
    margin-top: 12px;
    display: flex;
    align-items: flex-start;
  }

  .page-picker-warning {
    @include text-caption();
    color: get-color("headline");
    margin-top: 12px;
    display: flex;
    align-items: flex-start;

    strong {
      @include text-caption-bold();
    }
  }

  .page-picker-load-more {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
  }
</style>
